import React from 'react';
import { ActivityIndicator, StyleProp, TextStyle, TouchableOpacity, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Divider from '../../../components/Divider';
import { getVacancyStatus } from '../Vacancies/VacanciesCommons';
import Tooltip from '../../../components/Tooltip';
import { WindowInformation } from '../../../services/window-information';
import { formatRelativeTime } from '../DeacCommons';

interface Props {
    recentVacancies: RecentVacancy[];
    navigation: Navigation;
    isLoading: boolean;
}

export default function RecentVacanciesDashboard({ recentVacancies, navigation, isLoading }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    function limitedSizeText(text: string, style: StyleProp<TextStyle>) {
        if (text.length > 17) {
            const truncatedText = text.length > 17 ? `${text.substring(0, 17)}...` : text;
            return <Tooltip
                useSimpleBox
                style={{
                    color: theme.colors.white['500'],
                    padding: 4,
                    borderRadius: 12,
                    justifyContent: 'center',
                    zIndex: 9999,
                    background: theme.colors.neutral['500'],
                    top: 20
                }}
                tooltipContent={text}
                tooltipDirection='top'
            >
                <MyAppText style={style}>{truncatedText}</MyAppText>
            </Tooltip>;
        } else {
            return <MyAppText style={style}>{text}</MyAppText>;
        }
    }

    return (
        <View style={styles.recentVacancies}>
            <View style={styles.headerContainer}>
                <MyAppText style={styles.chartHeaderTitle}>{translate('recentVacancies')}</MyAppText>
                <TouchableOpacity onPress={() => navigation.navigate('Vacancies', { status: 'opened' })}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} fontSize={16} color={theme.colors.primary['500']} />
                </TouchableOpacity>
            </View>
            <View style={[{ flexDirection: 'column' }, isLoading && { flex: 1 }]}>
                {isLoading
                    ? <View style={styles.allSpaceCentered}>
                        <ActivityIndicator size='large' color={theme.colors.primary['500']}/>
                    </View>
                    : <>
                        {recentVacancies.length === 0 &&
                            <MyAppText style={styles.workplaceName}>{translate('noRecordsForSelectedPeriod')}</MyAppText>
                        }
                        {recentVacancies.map((vacancy, index) =>
                            <View key={index} style={styles.recentVacanciesContainer(index + 1)}>
                                <View style={styles.recentVacanciesContentContainer(index)}>
                                    <View style={styles.recentVacanciesTextContent}>
                                        <MyAppText style={styles.rowIndexText}>{index + 1}</MyAppText>
                                        <View style={{ flexDirection: 'column' }}>
                                            <MyAppText style={styles.activityTitle}>{vacancy.title}</MyAppText>
                                            {limitedSizeText(vacancy.workplace.name, styles.workplaceName)}
                                            {limitedSizeText(vacancy.levels.map(l => l.name).join(', '), styles.levels)}
                                        </View>
                                    </View>
                                    <View style={styles.recentVacanciesBadgeContent}>
                                        {getVacancyStatus({
                                            vacancy: {
                                                activityFinish: vacancy.activityFinish,
                                                activityStart: vacancy.activityStart,
                                                applicationsFinish: vacancy.applicationsFinish,
                                                applicationsStart: vacancy.applicationsStart,
                                            },
                                            size: windowInfo.width <= 1024 ? 'small' : 'medium',
                                            style: { alignSelf: 'flex-end' },
                                        })}
                                        <MyAppText style={styles.openingTime}>{formatRelativeTime(vacancy.applicationsStart)}</MyAppText>
                                    </View>
                                </View>
                                {index < recentVacancies.length - 1 ? <Divider style={{ marginVertical: 8 }} /> : null }
                            </View>
                        )}
                    </>}
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    headerContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
    },
    recentVacancies: {
        flex: 1,
        padding: 16,
        gap: 16,
        backgroundColor: theme.colors.white['500'],
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.neutral['100']
    },
    recentVacanciesContainer: (index: number) => ({
        flexDirection: 'column',
        zIndex: 17 - index
    }),
    recentVacanciesContentContainer: (index: number) => ({
        flexDirection: 'row',
        justifyContent: 'space-between',
        zIndex: 17 - index
    }),
    recentVacanciesTextContent: {
        flexDirection: 'row',
        gap: 16
    },
    recentVacanciesBadgeContent: {
        flexDirection: 'column',
        justifyContent: 'space-between'
    },
    chartHeaderTitle: {
        fontSize: 16,
        color: theme.colors.secondary['400'],
        fontWeight: '600',
    },
    rowIndexText: {
        fontWeight: '600',
        fontSize: 14,
        color: theme.colors.neutral['400'],
        alignSelf: 'center',
        width: 17
    },
    activityTitle: {
        fontSize: 14,
        color: theme.colors.secondary['400'],
        fontWeight: '600',
    },
    workplaceName: {
        fontSize: 14,
        color: theme.colors.neutral['500'],
        fontWeight: '600',
    },
    levels: {
        fontSize: 14,
        color: theme.colors.neutral['400'],
        fontWeight: '600',
    },
    openingTime: {
        fontSize: 10,
        color: theme.colors.neutral['400'],
        fontWeight: '600',
        alignSelf: 'flex-end'
    },
    allSpaceCentered: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    }
}));
